import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEODenseKeywords from '@/components/SEODenseKeywords';
import { blogPosts as mockPosts, blogMetadata } from '@/data/mockData';
import { ChevronRight, Calendar, User, Tag, Lock, X, Share2, Clock, Heart, Eye } from 'lucide-react';

const Blog = () => {
    const [posts, setPosts] = useState(mockPosts);
    const [selectedPost, setSelectedPost] = useState(null);

    // Filter out "Equipo" from names if requested, just "Evolvance"
    const authorName = "Evolvance";
    const authorLogo = "https://customer-assets.emergentagent.com/job_2d5da32d-c8d7-4484-9889-ef5971ddb3aa/artifacts/et1hsjc0_logo%2Btexto-evolvance.png";

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedPost) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedPost]);

    const fetchPosts = async () => {
        try {
            const response = await axios.get('http://localhost:8000/api/blog');
            if (response.data) {
                // We show both mock and live
                setPosts([...response.data, ...mockPosts]);
            }
        } catch (error) {
            console.warn("Backend not reached, using mock data only.");
        }
    };

    useEffect(() => {
        fetchPosts();

        // Dynamic SEO update for Blog
        document.title = blogMetadata.title;

        // Helper to update or create meta tags
        const updateMetaTag = (property, value, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let tag = document.querySelector(`meta[${attr}="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute(attr, property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', value);
        };

        updateMetaTag('description', blogMetadata.description);
        updateMetaTag('keywords', blogMetadata.keywords);
    }, []);

    const handleReadPost = async (post) => {
        setSelectedPost(post);
        // Register view in backend
        try {
            await axios.post(`http://localhost:8000/api/blog/${post.id}/view`);
            // Refresh counts locally for a better UI experience
            setPosts(prevPosts => prevPosts.map(p =>
                p.id === post.id ? { ...p, views: (p.views || 0) + 1 } : p
            ));
        } catch (e) {
            console.error("Error registering view:", e);
        }
    };

    return (
        <div className="min-h-screen bg-[#1A1B1B] text-white selection:bg-[#D4AF37] selection:text-black">
            <SEODenseKeywords />
            <Header />

            {/* Blog Hero Section */}
            <section className="pt-32 pb-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent pointer-events-none" />
                <div className="container-custom relative z-10">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-outfit font-bold mb-6 text-white leading-tight">
                            Blog de IA, <span className="text-[#D4AF37]">Tecnología</span> y Estrategia
                        </h1>
                        <p className="text-xl text-[#B1B1B1] font-outfit leading-relaxed">
                            Descubre las últimas tendencias, guías y casos de éxito sobre cómo la inteligencia artificial y la transformación digital están redefiniendo el éxito empresarial en 2025.
                        </p>
                    </div>
                </div>
            </section>

            {/* Blog Feed */}
            <section className="pb-32">
                <div className="container-custom">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {posts.map((post, index) => (
                            <article
                                key={post.id || index}
                                className="group bg-[#202121] rounded-2xl overflow-hidden border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-all duration-500 shadow-xl flex flex-col h-full"
                            >
                                {/* Post Image */}
                                <div className="relative aspect-video overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase px-3 py-1 rounded-full tracking-wider">
                                            {post.category}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-md px-3 py-1 rounded-full flex items-center gap-1.5 text-xs text-white">
                                        <Eye size={12} className="text-[#D4AF37]" />
                                        <span>{post.views || 0}</span>
                                    </div>
                                </div>

                                {/* Post Content */}
                                <div className="p-8 flex flex-col flex-grow">
                                    <div className="flex items-center gap-4 text-[#888] text-xs mb-4">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {post.date}
                                        </span>
                                        <span className="flex items-center gap-1 font-semibold text-[#D4AF37]">
                                            <Tag className="w-3.5 h-3.5" />
                                            {post.category}
                                        </span>
                                    </div>

                                    <h2 className="text-2xl font-outfit font-bold mb-4 leading-tight group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                                        {post.title}
                                    </h2>

                                    <p className="text-[#B1B1B1] text-sm leading-relaxed mb-6 line-clamp-3">
                                        {post.description}
                                    </p>

                                    <div className="mt-auto pt-6 flex items-center justify-between border-t border-white/5">
                                        <button
                                            onClick={() => handleReadPost(post)}
                                            className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm hover:gap-4 transition-all"
                                        >
                                            Leer artículo <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <div className="flex items-center gap-1.5 text-[#888] hover:text-[#D4AF37] transition-colors cursor-pointer group/heart">
                                            <Heart size={16} className="group-hover/heart:fill-[#D4AF37] transition-all" />
                                            <span className="text-xs">{(post.views || 0) > 10 ? '¡Top!' : ''}</span>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Admin Access Section */}
            <div className="py-20 text-center border-t border-white/5 bg-[#1F2020]">
                <div className="container-custom">
                    <p className="text-[#555] text-sm mb-4 font-outfit">¿Eres administrador? Gestiona el contenido aquí.</p>
                    <a
                        href="/#/blog-admin"
                        className="inline-flex items-center gap-2 px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10 transition-all font-outfit text-sm font-semibold uppercase tracking-widest"
                    >
                        <Lock className="w-4 h-4" /> Acceso Admin
                    </a>
                </div>
            </div>

            <Footer />

            {/* Post Reader Modal */}
            {selectedPost && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 animate-fade-in">
                    <div
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setSelectedPost(null)}
                    />

                    <div className="relative w-full max-w-4xl max-h-[90vh] bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-3xl overflow-hidden shadow-2xl flex flex-col animate-fade-in-up">
                        <button
                            onClick={() => setSelectedPost(null)}
                            className="absolute top-6 right-6 z-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all border border-white/10"
                        >
                            <X size={24} />
                        </button>

                        <div className="overflow-y-auto custom-scrollbar">
                            <div className="relative h-64 md:h-96 w-full">
                                <img
                                    src={selectedPost.image}
                                    alt={selectedPost.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B1B] via-transparent to-transparent" />
                                <div className="absolute bottom-8 left-8 right-8">
                                    <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase px-3 py-1 rounded-full tracking-wider mb-4 inline-block">
                                        {selectedPost.category}
                                    </span>
                                    <h2 className="text-3xl md:text-5xl font-outfit font-bold text-white leading-tight">
                                        {selectedPost.title}
                                    </h2>
                                </div>
                            </div>

                            <div className="p-8 md:p-12">
                                <div className="flex flex-wrap items-center justify-between gap-6 text-[#888] text-sm mb-10 border-b border-white/5 pb-6">
                                    <div className="flex flex-wrap gap-6">
                                        <span className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-[#D4AF37]" />
                                            {selectedPost.date}
                                        </span>
                                        <span className="flex items-center gap-2">
                                            <Eye className="w-4 h-4 text-[#D4AF37]" />
                                            {selectedPost.views || 0} lecturas
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 group/heart cursor-pointer border border-white/10 px-3 py-1 rounded-full hover:border-[#D4AF37]/30 transition-all">
                                        <Heart size={16} className="text-[#D4AF37] group-hover/heart:fill-[#D4AF37]" />
                                        <span className="text-[#D4AF37] font-bold">Me gusta</span>
                                    </div>
                                </div>

                                <div className="prose prose-invert prose-gold max-w-none">
                                    <p className="text-xl text-[#DBCE97] font-outfit leading-relaxed mb-8 italic">
                                        {selectedPost.description}
                                    </p>

                                    <div
                                        className="text-[#B1B1B1] leading-loose space-y-6 font-manrope text-lg blog-content-reveal whitespace-pre-wrap"
                                        dangerouslySetInnerHTML={{
                                            __html: selectedPost.content || `
                                            <p>Este es el contenido detallado del artículo sobre ${selectedPost.title}.</p>
                                            <p>En este post exploramos cómo la tecnología está transformando el sector de ${selectedPost.category} y qué pasos deberían seguir las empresas para no quedarse atrás en 2025.</p>
                                        `}}
                                    />
                                </div>

                                <div className="mt-16 pt-8 border-t border-white/5 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#D4AF37]/30">
                                            <img src="/logo-evolvance.png" alt="Evolvance" className="w-full h-full object-cover bg-black" />
                                        </div>
                                        <div>
                                            <p className="text-white font-bold text-sm">{authorName}</p>
                                            <p className="text-[#888] text-xs">Consultoría Tecnológica</p>
                                        </div>
                                    </div>
                                    <button className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors text-sm font-bold">
                                        <Share2 size={18} /> Compartir
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Blog;
